/**
 * Type definitions for the Admin Dashboard
 */

export type TimeRange = '1d' | '3d' | '7d' | '30d';

export const TIME_RANGE_LABELS: Record<TimeRange, string> = {
  '1d': 'Last day',
  '3d': 'Last 3 days',
  '7d': 'Last 7 days',
  '30d': 'Last 30 days',
};

export interface DataPoint {
  timestamp: string;
  value: number;
}

export interface MetricsSummary {
  totalQueries: number;
  avgLatencyMs: number;
  avgTimeToFirstTokenMs: number;
  errorRate: number;
  avgDocumentsRetrieved: number;
  postFilterRejectionRate: number;
}

export interface TokenSummary {
  totalInputTokens: number;
  totalOutputTokens: number;
  totalTokens: number;
  avgTokensPerQuery: number;
}

export interface IngestionSummary {
  totalDocsIngested: number;
  totalWebhooks: number;
  totalSyncJobs: number;
  totalIngestionErrors: number;
}

export interface ClassificationByTier {
  contactMatch: number;
  nameMatch: number;
  keywordMatch: number;
  semanticMatch: number;
  none: number;
}

export interface ClassificationSummary {
  totalAttempts: number;
  totalCompleted: number;
  avgLatencyMs: number;
  avgConfidence: number;
  matchRate: number;
  pendingReview: number;
  totalErrors: number;
  totalEscalations: number;
  multipleClientMatches: number;
  byTier: ClassificationByTier;
}

export interface SyncHandlerSummary {
  totalInvocations: number;
  totalErrors: number;
  ingestionJobsStarted: number;
  ingestionJobsAlreadyRunning: number;
  ingestionJobErrors: number;
}

export interface SyncWorkerSummary {
  totalItemsSynced: number;
  totalItemsSkipped: number;
  totalItemsFailed: number;
  totalDurationSeconds: number;
  avgProcessingRate: number;
  totalApiCalls: number;
  totalS3Saves: number;
}

export interface TimeSeries {
  queryVolume: DataPoint[];
  retrievalLatency: DataPoint[];
  llmLatency: DataPoint[];
  timeToFirstToken: DataPoint[];
  errors: DataPoint[];
  postFilterRejections: DataPoint[];
  documentsIngested: DataPoint[];
  webhooksReceived: DataPoint[];
  classifications: DataPoint[];
  // Token usage for cost monitoring
  inputTokens: DataPoint[];
  outputTokens: DataPoint[];
}

// Dimensional breakdown types
export interface ErrorByType {
  type: string;
  count: number;
}

export interface IngestionBySourcePoint {
  timestamp: string;
  fathom: number;
  helpscout: number;
  basecamp: number;
}

export interface SyncOutcomeBySource {
  source: string;
  synced: number;
  skipped: number;
  failed: number;
}

// Latency percentile data for SLA monitoring
export interface PercentileValues {
  p50: number;
  p75: number;
  p90: number;
  p95: number;
  p99: number;
}

export interface LatencyPercentiles {
  retrieval: PercentileValues;
  llm: PercentileValues;
  ttft: PercentileValues;
}

export interface MetricsResponse {
  timeRange: TimeRange;
  period: number;
  generatedAt: string;
  summary: MetricsSummary;
  tokenSummary: TokenSummary;
  ingestionSummary: IngestionSummary;
  classificationSummary: ClassificationSummary;
  syncHandlerSummary: SyncHandlerSummary;
  syncWorkerSummary: SyncWorkerSummary;
  timeSeries: TimeSeries;
  // Dimensional breakdowns
  errorsByType: ErrorByType[];
  ingestionBySource: IngestionBySourcePoint[];
  syncOutcomesBySource: SyncOutcomeBySource[];
  // Latency percentiles for SLA monitoring
  latencyPercentiles: LatencyPercentiles;
  logGroups: string[];
}

export type LogLevel = 'INFO' | 'WARN' | 'ERROR';

export interface LogEvent {
  timestamp: string;
  message: string;
  logStreamName: string;
  level?: LogLevel;
  requestId?: string;
  /** Structured data from JSON logs */
  data?: Record<string, unknown>;
}

export interface LogsResponse {
  logGroup: string;
  events: LogEvent[];
  totalCount: number;
  offset: number;
  hasMore: boolean;
  timeRange?: TimeRange;
}

export const LOG_GROUPS = [
  '/aws/lambda/nb-rag-arts-way-chat',
  '/aws/lambda/nb-rag-arts-way-classification',
  '/aws/lambda/nb-rag-arts-way-dashboard',
  '/aws/lambda/nb-rag-arts-way-ingest',
  '/aws/lambda/nb-rag-arts-way-management',
] as const;

export type LogGroup = (typeof LOG_GROUPS)[number];

// Cost Explorer Types (uses shared TimeRange)

export interface CostByService {
  service: string;
  cost: number;
  percentage: number;
}

export interface DailyCost {
  date: string;
  cost: number;
  byService: Record<string, number>;
}

export interface CostsSummary {
  totalCost: number;
  dailyAverage: number;
  projectedMonthly: number;
}

export interface CostsResponse {
  timeRange: TimeRange;
  generatedAt: string;
  currency: string;
  summary: CostsSummary;
  byService: CostByService[];
  daily: DailyCost[];
  message?: string;
}

export interface DashboardState {
  metrics: MetricsResponse | null;
  logs: LogsResponse | null;
  isLoadingMetrics: boolean;
  isLoadingLogs: boolean;
  metricsError: string | null;
  logsError: string | null;
}
